// Pages are wired as a NAMESPACE for frank (the platform serializer): this
// package's pages are named page objects the workspace shell imports directly
// and maps onto ITS route table — the package never owned routes. Frank's
// extract guard compares pages/*.js on disk against extracted entries and
// refuses to push when files exist but zero entries extract (the guard that
// stopped the 2026-04 silverbreeze zero-page overwrites). A namespace keeps it
// honest without fabricating a route map: entries are keyed by export name,
// not '/route', and shared-library merge only auto-mounts ROUTE keys, so
// consumers cannot accidentally mount these.
import * as components from './components/index.js'
import * as pages from './pages/index.js'
import * as functions from './functions/index.js'
import * as methods from './methods/index.js'
import * as state from './state.js'
import designSystem from './designSystem/index.js'
import dependencies from './dependencies.js'

// The interface-translation config, and the ONE place it may live.
//
// It used to sit on the workspace shell's own context. Measured with
// `frank.toJSON` on packages/workspace: `defaultLang: ""`, `languages: ""`,
// `translations.en: 0 keys`. `smbls` and every `@symbo-ls/*` package are on
// frank's EXTERNAL_PACKAGES list, so a value the shell imported from one of
// them and dropped straight into a section bag published as `""`. The shell
// shipped a polyglot config with no default language, no language list and no
// English base — silently, because the build itself did not throw.
//
// It cannot be repaired from outside either. `mergeOneLibrary`
// (smbls/packages/utils/sharedLibraries.js) fills a consumer's key only when
// `!(k in context[key])`, and its stub-phantom rescue covers OBJECT_BAGS
// (components/pages/functions/snippets/methods) alone — `polyglot` is not in
// that set, so the shell's own `""` would have beaten a real library value.
// The config must not be declared stubbed in the first place.
//
// HERE it is project-local: `functions/languages.js` is the platform's
// language registry and `i18nStaticEnglish()` assembles the English base from
// `i18n/en/*.json`, both inside this package, so frank serializes real values.
// And because `mergeOneLibrary`'s last branch copies a whole top-level key when
// the consumer does not define it (`else if (!(key in context))`), every
// consumer inherits the real object — provided no consumer declares a
// `polyglot` of its own. All five apps declare this library (workspace, canvas,
// marketplace, preview, analytics), so all five get translations, not just the
// shell. That matters beyond tidiness: the account page also mounts under
// canvas, and `replaceLiteralsWithObjectFields` (smbls/packages/utils/
// string.js) returns '' — an EMPTY label, not the key — when the `polyglot`
// filter is not registered.
//
// No `plugins` entry here on purpose. `createDomql` registers the polyglot
// plugin AND its functions itself whenever it sees `context.polyglot` and no
// plugin named `polyglot`. Importing the plugin to declare it would reintroduce
// the very cross-package stub this move exists to remove.
//
// `storageLangKey` MUST be `lang`: polyglot's own default is `smbls_lang`, and
// `initPolyglot` reads this key on the root render. Left at the default it
// would read an unset key, fall back to `defaultLang`, and write English over
// whatever bootShell resolved.
//
// `translations` carries THE BASE LANGUAGE ONLY — English — bundled, never
// fetched. It is the fallback for every miss, and
// `replaceLiteralsWithObjectFields` / polyglot's `translate()` return the KEY
// ITSELF when no step resolves, so a base language that needed a round trip
// would put a raw `common.action.save` on screen the first time anything went
// wrong. Every OTHER language is a lazily-imported, content-hashed chunk per
// namespace: `functions/i18nLoader.js` holds the registry, the Parcel
// measurements behind the split, and the reason a template-literal dynamic
// import is not an option.
//
// It carried every shipped language until 2026-09-02. That was right at 77
// keys and wrong at the platform's target size: English alone is ~100 KB at
// ~2900 keys and each further language would add ~120 KB to the FIRST paint of
// every visitor, in a language almost none of them will pick.
//
// `namespaces` is the LOAD list — the bundles this session needs in whatever
// language it ends up in. bootShell narrows it per route from the running
// app's manifest (`i18nNamespaces`) and `setLang` re-loads exactly that set on
// a switch. Declared HERE as well so it exists on every context, including one
// bootShell never ran for (a sub-app mounted by the shell's dispatch gets the
// library merge but not the boot sequence).
//
// ON A PUBLISHED RECORD there are no chunks to import, so a non-English
// language must be BAKED into this object to be switchable there. English is
// baked by construction: frank runs this module body in Node, so
// `i18nStaticTranslations()` returns real strings and they serialize as data.
const polyglot = {
  defaultLang: functions.DEFAULT_LANG,
  languages: functions.PLATFORM_LANGUAGES.map((l) => l.code),
  storageLangKey: 'lang',
  namespaces: [
    'common',
    'auth',
    'account',
    'settings',
    // Added with the shared-package extraction (2026-09-02). Every one of
    // these has real en+ka bundles on disk; a namespace listed here but not
    // shipped is dropped by the loader's own intersection, and one SHIPPED but
    // not listed here resolves English only — the switch simply never reaches
    // it, silently, because English is bundled.
    'shell',
    'editor',
    'members',
    'learn',
    'ai',
    'publish',
    'apps',
    'workspace',
    'admin',
    'data',
    // Store copy is SHELL CHROME (2026-09-03) — kept in step with bootShell's
    // own BOOT_NAMESPACES, which a parity gate pins
    // (__tests__/i18nBootMountDelivery.test.js). Every installed app's navbar
    // tab and launcher tile resolves its label through the one store-copy
    // resolver (`functions/storeItemText.js` -> `marketplace.app.<slug>.name`),
    // so an app reads the same name here as in the store it came from.
    'marketplace'
  ],
  translations: functions.i18nStaticTranslations()
}

// The runtime packages this library dynamic-imports (dependencies.js), on the
// platform record. frank builds the payload from THIS module, so a package
// that dependencies.js declares but this export leaves out never reaches the
// system/shared record (measured 2026-10-31 on a dev push rehearsal: the only
// dependency op was `prismjs: "latest"`, the 6 pins were lost).
//
// `dependenciesOnDemand` lists the SAME pins (Nika 2026-10-31: "each loaded
// only when a page uses it"). Every entry is an `await import('<pkg>')`
// inside a handler (FA206), so nothing needs it before the first render.
// smbls prepare.js: `prepareDependencies` skips an on-demand entry and
// `splitDependencies` never defers it to the after-render preload, so no page
// loads these 6 at boot. `getImportMapScript` (@symbo.ls/utils cdn.js) reads
// `dependencies` only, so this library's own page still maps each name and
// `import('<pkg>')` resolves on use. `mergeOneLibrary` copies both keys into
// a consumer the same way (add-missing), and the on-demand key acts by name:
// a consumer's or another library's entry of the same name is not preloaded
// either. __tests__/contextDependenciesOnDemand.test.js pins all of this.
//
// A CONSUMER page resolves `import('<pkg>')` only when its OWN importmap maps
// the name: mermaid serves libraries unmerged (mermaid bundle.js) and builds
// the importmap from the consumer's `dependencies` alone, and frank keeps
// the handler's import literal (no `__SMBLS_PKGS__` read), so a boot preload
// of a library entry never made that import resolve. Dropping the preload
// costs a consumer nothing; mapping library entries into a consumer's
// importmap is a framework (mermaid) change.
export default {
  pages,
  components,
  functions,
  methods,
  state,
  designSystem,
  dependencies,
  dependenciesOnDemand: { ...dependencies },
  polyglot
}
